import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DomainIcon from '@mui/icons-material/Domain';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

import type { NavItem } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { FEATURE } from '@/core/features/featureCodes';

/**
 * URL slugs for launcher modules — keep in sync with {@link ModuleEntry} in moduleEntries.ts.
 * Defined here (not in moduleEntries) to avoid a circular import at startup.
 */
export const ERP_MODULE_SLUGS: Partial<Record<ErpModule, string>> = {
  POS: 'pos',
  CRM: 'crm',
  HR: 'hr',
  DESK: 'desk',
  FINANCE: 'finance',
  PROJECTS: 'projects',
  PROCUREMENT: 'procurement',
  WAREHOUSE: 'warehouse',
  SOCIAL: 'social',
  LEAD_GEN: 'lead-gen',
  MARKETING: 'marketing',
};

export const LAUNCHER_ERP_MODULES = Object.keys(ERP_MODULE_SLUGS) as ErpModule[];

export type ModuleAdminPage =
  | 'administration'
  | 'profile'
  | 'companies'
  | 'users'
  | 'roles'
  | 'settings'
  | 'subscription';

/** Legacy org-wide admin paths (redirect to module-scoped routes). */
export const LEGACY_ORG_ADMIN_ROUTE_PREFIXES = [
  '/org/administration',
  '/org/profile',
  '/org/employees',
  '/org/roles',
  '/org/workspace',
  '/org/subscription',
  '/org/settings',
] as const;

export const MODULE_DEFAULT_API_PREFIXES = [
  '/v1/employees',
  '/v1/roles',
  '/v1/permissions',
  '/v1/companies',
] as const;

export const MODULE_DEFAULT_PERMISSION_MODULES = [
  'EMPLOYEE',
  'ROLE',
  'ORGANIZATION',
  'DASHBOARD',
  'REPORT',
] as const;

/** @deprecated Use moduleAdminPath(module, 'settings') */
export const MODULE_SETTINGS_PATH = '/org/workspace/settings';
/** @deprecated Use moduleAdminPath(module, 'subscription') */
export const MODULE_SUBSCRIPTION_PATH = '/org/workspace/subscription';

export function moduleSlug(erpModule: ErpModule): string {
  const slug = ERP_MODULE_SLUGS[erpModule];
  if (slug) return slug;
  return erpModule.toLowerCase().replace(/_/g, '-');
}

/** Platform login users segment — HR uses `users` because `/hr/employees` is HR employee records. */
export function moduleUsersSegment(erpModule: ErpModule): 'employees' | 'users' {
  return erpModule === 'HR' ? 'users' : 'employees';
}

export function moduleAdminPath(erpModule: ErpModule, page: ModuleAdminPage): string {
  const slug = moduleSlug(erpModule);
  if (page === 'users') {
    return `/${slug}/${moduleUsersSegment(erpModule)}`;
  }
  return `/${slug}/${page}`;
}

/** Module-scoped administration routes for a single ERP module. */
export function moduleAdminRoutes(erpModule: ErpModule): string[] {
  return [
    moduleAdminPath(erpModule, 'administration'),
    moduleAdminPath(erpModule, 'profile'),
    moduleAdminPath(erpModule, 'companies'),
    moduleAdminPath(erpModule, 'users'),
    moduleAdminPath(erpModule, 'roles'),
    moduleAdminPath(erpModule, 'settings'),
    moduleAdminPath(erpModule, 'subscription'),
    MODULE_SETTINGS_PATH,
    MODULE_SUBSCRIPTION_PATH,
  ];
}

export function moduleDefaultRoutes(erpModule: ErpModule, ...extras: string[]): string[] {
  return [
    ...moduleAdminRoutes(erpModule),
    ...LEGACY_ORG_ADMIN_ROUTE_PREFIXES,
    '/dashboard/home',
    '/onboarding',
    ...extras,
  ];
}

export function moduleDefaultApis(...extras: string[]): string[] {
  return [...MODULE_DEFAULT_API_PREFIXES, ...extras];
}

export function moduleDefaultPermissionModules(...extras: string[]): string[] {
  return [...new Set([...MODULE_DEFAULT_PERMISSION_MODULES, ...extras])];
}

/** Legacy `/org/*` admin URLs and global onboarding — not used for active-module inference. */
export function isPlatformAdminRoute(pathname: string): boolean {
  if (pathname === '/org' || pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return true;
  }
  return LEGACY_ORG_ADMIN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function resolveModuleFromAdminPath(pathname: string): ErpModule | null {
  for (const erpModule of LAUNCHER_ERP_MODULES) {
    const routes = moduleAdminRoutes(erpModule);
    if (routes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return erpModule;
    }
  }
  return null;
}

export function resolveModuleAdminRoutePermission(
  pathname: string,
): string | string[] | null | undefined {
  const module = resolveModuleFromAdminPath(pathname);
  if (!module) return undefined;
  const slug = moduleSlug(module);
  const usersPath = `/${slug}/${moduleUsersSegment(module)}`;
  if (pathname === usersPath || pathname.startsWith(`${usersPath}/`)) {
    return 'EMPLOYEE_VIEW';
  }
  if (pathname === `/${slug}/roles` || pathname.startsWith(`/${slug}/roles/`)) {
    return 'ROLE_VIEW';
  }
  if (pathname === `/${slug}/profile` || pathname.startsWith(`/${slug}/profile/`)) {
    return 'ORG_VIEW';
  }
  if (pathname === `/${slug}/companies` || pathname.startsWith(`/${slug}/companies/`)) {
    return 'COMPANY_VIEW';
  }
  if (pathname === `/${slug}/administration` || pathname.startsWith(`/${slug}/administration/`)) {
    return 'ORG_MANAGE';
  }
  if (pathname === `/${slug}/subscription` || pathname.startsWith(`/${slug}/subscription/`)) {
    return 'SUBSCRIPTION_MANAGE';
  }
  if (pathname === `/${slug}/settings` || pathname.startsWith(`/${slug}/settings/`)) {
    return null;
  }
  return undefined;
}

export function resolveModuleAdminRouteFeature(pathname: string): string | null | undefined {
  const module = resolveModuleFromAdminPath(pathname);
  if (!module) return undefined;
  const slug = moduleSlug(module);
  const usersPath = `/${slug}/${moduleUsersSegment(module)}`;
  if (pathname === usersPath || pathname.startsWith(`${usersPath}/`)) {
    return FEATURE.EMPLOYEE_BASIC;
  }
  if (pathname === `/${slug}/roles` || pathname.startsWith(`/${slug}/roles/`)) {
    return FEATURE.EMPLOYEE_BASIC;
  }
  if (
    pathname === `/${slug}/administration` ||
    pathname === `/${slug}/settings` ||
    pathname === `/${slug}/subscription` ||
    pathname === `/${slug}/profile`
  ) {
    return null;
  }
  return undefined;
}

/** Administration sidebar items for a module workspace. */
export function getModuleAdminNavItems(module: ErpModule): NavItem[] {
  return [
    { to: moduleAdminPath(module, 'administration'), label: 'Workspace home', icon: DashboardIcon },
    { to: moduleAdminPath(module, 'profile'), label: 'Organization', icon: BusinessIcon },
    { to: moduleAdminPath(module, 'companies'), label: 'Companies', icon: DomainIcon },
    {
      to: moduleAdminPath(module, 'users'),
      label: 'Users',
      icon: GroupsIcon,
      feature: FEATURE.EMPLOYEE_BASIC,
    },
    {
      to: moduleAdminPath(module, 'roles'),
      label: 'Roles & permissions',
      icon: SecurityIcon,
      feature: FEATURE.EMPLOYEE_BASIC,
    },
    { to: moduleSubscriptionLink(module), label: 'Subscription', icon: SubscriptionsIcon },
    { to: moduleSettingsLink(module), label: 'Module settings', icon: SettingsIcon },
  ];
}

/** @deprecated Use getModuleAdminNavItems(module) */
export function moduleAdminNavItems(module: ErpModule = 'POS'): NavItem[] {
  return getModuleAdminNavItems(module).filter(
    (item) =>
      item.to !== moduleAdminPath(module, 'administration') &&
      item.to !== moduleSubscriptionLink(module) &&
      item.to !== moduleSettingsLink(module),
  );
}

export function moduleSettingsLink(module: ErpModule): string {
  return moduleAdminPath(module, 'settings');
}

export function moduleSubscriptionLink(module: ErpModule): string {
  return moduleAdminPath(module, 'subscription');
}

export function moduleWorkspaceNavItems(module: ErpModule): NavItem[] {
  return [
    { to: moduleSubscriptionLink(module), label: 'Subscription', icon: SubscriptionsIcon },
    { to: moduleSettingsLink(module), label: 'Settings', icon: SettingsIcon },
  ];
}

export function dedupeNavItems(items: NavItem[]): NavItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.to.split('?')[0];
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Map legacy `/org/...` admin path segment to module-scoped path. */
export function legacyOrgAdminRedirectTarget(
  pathname: string,
  module: ErpModule,
): string | null {
  if (pathname === '/org/administration' || pathname.startsWith('/org/administration/')) {
    if (pathname === '/org/administration/billing') {
      return moduleAdminPath(module, 'subscription');
    }
    return moduleAdminPath(module, 'administration');
  }
  if (pathname === '/org/profile' || pathname.startsWith('/org/profile/')) {
    return moduleAdminPath(module, 'profile');
  }
  if (pathname === '/org/employees' || pathname.startsWith('/org/employees/')) {
    return moduleAdminPath(module, 'users');
  }
  if (pathname === '/org/roles' || pathname.startsWith('/org/roles/')) {
    return moduleAdminPath(module, 'roles');
  }
  if (pathname === '/org/subscription' || pathname.startsWith('/org/subscription/')) {
    return moduleAdminPath(module, 'subscription');
  }
  if (pathname === '/org/workspace/subscription' || pathname.startsWith('/org/workspace/subscription')) {
    return moduleAdminPath(module, 'subscription');
  }
  if (pathname === '/org/workspace/settings' || pathname.startsWith('/org/workspace/settings')) {
    return moduleAdminPath(module, 'settings');
  }
  if (pathname === '/org/settings' || pathname.startsWith('/org/settings/')) {
    return moduleAdminPath(module, 'settings');
  }
  return null;
}
